import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { scoreTone } from "@/lib/api";
import { CATEGORY_META } from "@/data/content";
import { ScoreRing, ScoreBar } from "@/components/ScoreRing";
import TransformationPlan from "@/components/TransformationPlan";
import ServiceRecommendations from "@/components/ServiceRecommendations";
import { useAnalyzer } from "@/context/AnalyzerContext";
import MakeoverTool from "@/components/MakeoverTool";

const PRIORITY_STYLE = {
  critical: { label: "Critical", color: "#EF4444", bg: "#FEF2F2" },
  high: { label: "High Priority", color: "#F59E0B", bg: "#FFFBEB" },
  recommended: { label: "Recommended", color: "#2563EB", bg: "#EFF6FF" },
  optional: { label: "Opportunity", color: "#64748B", bg: "#F8FAFC" },
};

const LOADING_STEPS = [
  "Fetching page & headers…",
  "Checking HTTPS, SSL & redirects…",
  "Parsing SEO metadata & headings…",
  "Auditing performance & page weight…",
  "Scoring accessibility & conversion…",
  "Compiling WebsiteTech Score…",
];

export default function AnalyzerResults() {
  const { phase, audit, plan, planLoading, openLead, openConsult, reset, generatePlan } = useAnalyzer();

  if (phase === "idle") return null;

  const tone = audit ? scoreTone(audit.overall_score) : null;
  const change = audit && audit.previous_score != null ? audit.overall_score - audit.previous_score : null;

  return (
    <section id="analysis-results" className="relative z-10 py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatePresence>
          {phase === "loading" && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-white border border-slate-200 rounded-2xl card-shadow p-8">
              <div className="flex items-center gap-3">
                <Loader2 className="animate-spin text-brand" size={20} />
                <span className="font-medium text-slate-600">Running WebsiteTech Website Intelligence™…</span>
              </div>
              <div className="mt-4 space-y-2">
                {LOADING_STEPS.map((s, i) => (
                  <motion.p key={s} initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.5 }} className="font-mono text-xs text-slate-400">{s}</motion.p>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {phase === "results" && audit && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {audit.content_readable === false && (
              <div className="mb-6 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3.5" data-testid="analyzer-unreadable-notice">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">
                  We couldn't fully read <span className="font-semibold">{audit.domain}</span>
                  {audit.block_reason === "bot-protection" || audit.block_reason === "blocked" ? " — it blocks automated visits" : audit.block_reason === "thin-or-js-rendered" ? " — its content is mostly JavaScript-rendered" : ""}.
                  {" "}These results are based on limited public info, so some scores may be understated. Re-scan once the page is reachable for a precise report.
                </p>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-10">
              <div className="flex flex-col lg:flex-row gap-10 items-center lg:items-start">
                <div className="text-center shrink-0">
                  <ScoreRing score={audit.overall_score} size={200} />
                  <p className="overline mt-3">WebsiteTech Score</p>
                  {change != null && change !== 0 && (
                    <div className="mt-1 font-mono text-sm font-semibold" style={{ color: change > 0 ? "#16A34A" : "#EF4444" }}>
                      {change > 0 ? "↑" : "↓"} {Math.abs(change)} vs previous
                    </div>
                  )}
                </div>
                <div className="flex-1 w-full">
                  <span className="overline">WebsiteTech Preliminary Analysis</span>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-1 break-all">{audit.domain}</h3>
                  <p className="text-slate-500 mt-2">
                    <span className="font-semibold" style={{ color: tone.color }}>{audit.opportunity_count} opportunities</span> identified across 8 categories.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                    {CATEGORY_META.map((c) => (
                      <ScoreBar key={c.key} label={c.label} score={audit.categories[c.key]} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <span className="overline">Priority Recommendations</span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {audit.recommendations.slice(0, 8).map((r, i) => {
                    const ps = PRIORITY_STYLE[r.priority];
                    return (
                      <div key={i} className="border border-slate-200 rounded-xl p-4 flex gap-3 bg-white" data-testid={`rec-${i}`}>
                        <span className="mt-0.5 w-1 self-stretch rounded shrink-0" style={{ backgroundColor: ps.color }} />
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded" style={{ color: ps.color, background: ps.bg }}>{ps.label}</span>
                            <span className="text-[10px] text-slate-400 font-semibold uppercase">{r.category}</span>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{r.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!plan && (
                <div className="mt-8 border-t border-slate-200 pt-8 flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-1">
                    <p className="font-heading text-xl md:text-2xl font-bold text-slate-900">See your complete website transformation plan</p>
                    <p className="text-slate-500 text-sm mt-1">AI-generated, phase-by-phase, based on this audit.</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={reset} className="btn-outline rounded-xl px-5 py-3.5 flex items-center gap-2"><RotateCcw size={15} /> New</button>
                    <button onClick={openLead} disabled={planLoading} data-testid="see-plan-btn" className="btn-primary rounded-xl px-7 py-3.5 flex items-center gap-2">
                      {planLoading ? <Loader2 className="animate-spin" size={18} /> : <>See Transformation Plan <ArrowUpRight size={16} /></>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {planLoading && !plan && (
              <div className="mt-6 bg-white border border-slate-200 rounded-2xl card-shadow p-8 flex items-center gap-3">
                <Loader2 className="animate-spin text-brand" size={20} />
                <span className="font-medium text-slate-600">WebsiteTech AI is drafting your transformation plan…</span>
              </div>
            )}

            <ServiceRecommendations categories={audit.categories} />

            <TransformationPlan plan={plan} onCTA={openConsult} />
            <MakeoverTool domain={audit.domain} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
